/**
 * Sticky / floating mobile Add to Cart bar — KHAITE-style inline size picker.
 *
 * - Bar stays out of the way until the real Add to Cart button has fully
 *   scrolled above the viewport, then appears; hides again once that
 *   button is back in view.
 * - Price and disabled state stay in sync with the real product form via
 *   the theme's existing `variantChange` / `variantPriceChange` events
 *   (see assets/theme.js).
 * - The CTA button is a state machine:
 *     no size chosen  -> "Select a Size"  -> tap opens the in-bar chip row
 *     size chosen     -> "Add to Cart"    -> tap adds to cart
 *   Nothing about this ever scrolls the page — the whole flow happens
 *   inside the fixed bar, matching KHAITE's mobile PDP.
 * - Tapping a size chip sets the matching real `[data-variant-input]`
 *   radio in the on-page size picker to checked and dispatches a native
 *   `change` event on it, so the theme's own Variants class runs its full
 *   pipeline (price, images, SKU, main Add to Cart button state) exactly
 *   as if the shopper had tapped it on the page. The chip row then
 *   collapses and the CTA swaps to "Add to Cart".
 * - If the product has no size option at all, the bar behaves like a
 *   normal Add to Cart button (no chip row, no "Select a Size" state).
 * - Sold-out sizes are crossed out in the chip row exactly like the real
 *   on-page size picker. The initial `disabled` attribute rendered by
 *   sticky-add-to-cart.liquid is only accurate for single-option products
 *   (Liquid can't know which sizes are sold out for the *currently
 *   selected* color on a Color + Size product ahead of time), so this
 *   script mirrors the live availability of the real, on-page size labels
 *   onto the matching chips -- once on init, and again any time that
 *   availability changes (e.g. after switching color), so the two pickers
 *   never fall out of sync.
 * - After a successful add, the CTA resets to "Select a Size" (rather
 *   than staying on "Add to Cart") so a shopper who wants a second size
 *   can immediately pick another one and add it too, without the bar
 *   silently re-adding whatever was last selected. Success is detected
 *   via the theme's own `ajaxProduct:added` event (assets/theme.js) —
 *   the same event the cart drawer listens for — so this only fires once
 *   the item has actually been added, not just clicked.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return cents;
  }

  function initStickyAddToCart(bar) {
    var sectionId = bar.getAttribute('data-product-section');
    var productSection = sectionId ? document.getElementById(sectionId) : null;

    if (!productSection) {
      return;
    }

    var mainAddToCartBtn = productSection.querySelector('[data-add-to-cart]');

    if (!mainAddToCartBtn) {
      return;
    }

    var variantPicker =
      productSection.querySelector('.variant-wrapper--size') ||
      productSection.querySelector('.product-block--variant-picker');

    var sizeInputs = variantPicker
      ? Array.prototype.slice.call(variantPicker.querySelectorAll('[data-variant-input]'))
      : [];

    var stickyButton = bar.querySelector('[data-sticky-atc-button]');
    var stickyButtonText = bar.querySelector('[data-sticky-atc-button-text]');
    var stickyPrice = bar.querySelector('[data-sticky-atc-price]');
    var stickyComparePrice = bar.querySelector('[data-sticky-atc-compare-price]');
    var sizesPanel = bar.querySelector('[data-sticky-atc-sizes]');
    var sizeChips = sizesPanel
      ? Array.prototype.slice.call(sizesPanel.querySelectorAll('[data-sticky-size-chip]'))
      : [];

    // Product has a size option only if the bar actually rendered a chip
    // panel for it (see snippets/sticky-add-to-cart.liquid).
    var hasSizeOption = !!sizesPanel && sizeChips.length > 0;

    // If there's no size option, there's nothing to "confirm" before
    // adding to cart — mirror the plain-button behavior.
    var variantConfirmed = !hasSizeOption;

    // Mirrors the real, on-page size picker's sold-out state onto the
    // matching sticky chip. same-variant-button.liquid adds a "disabled"
    // class to a size's <label> (inside the .variant-input wrapper for
    // that value) whenever that value isn't purchasable for the
    // currently selected combination of the other options -- the same
    // signal the real picker uses to cross a size out. Reading that
    // signal here (rather than recomputing availability ourselves) means
    // this stays correct for any option shape without duplicating the
    // theme's own variant-matrix logic.
    function syncSizeAvailability() {
      if (!hasSizeOption || !variantPicker) {
        return;
      }

      sizeChips.forEach(function (chip) {
        var value = chip.getAttribute('data-value');
        var wrap = variantPicker.querySelector(
          '.variant-input[data-value="' + (window.CSS && CSS.escape ? CSS.escape(value) : value) + '"]'
        );
        var label = wrap && wrap.querySelector('.variant__button-label');
        var isSoldOut = !!(label && label.classList.contains('disabled'));

        chip.disabled = isSoldOut;
        chip.classList.toggle('sticky-atc__size-chip--unavailable', isSoldOut);
      });
    }

    function setButtonLabel(text) {
      if (stickyButtonText) {
        stickyButtonText.textContent = text;
      }
    }

    function openSizes() {
      if (!sizesPanel) {
        return;
      }
      sizesPanel.classList.add('sticky-atc__sizes--open');
    }

    function closeSizes() {
      if (!sizesPanel) {
        return;
      }
      sizesPanel.classList.remove('sticky-atc__sizes--open');
    }

    function syncChipSelectedState(value) {
      sizeChips.forEach(function (chip) {
        chip.setAttribute('data-selected', chip.getAttribute('data-value') === value ? 'true' : 'false');
      });
    }

    // Tapping a chip drives the *real* size input on the page, so the
    // theme's own Variants class handles price/image/SKU/availability
    // updates exactly as it would for a normal on-page tap.
    sizeChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.disabled) {
          return;
        }

        var value = chip.getAttribute('data-value');
        var matchingInput = sizeInputs.filter(function (input) {
          return input.value === value;
        })[0];

        if (!matchingInput) {
          return;
        }

        matchingInput.checked = true;
        matchingInput.dispatchEvent(new Event('change', { bubbles: true }));

        syncChipSelectedState(value);
        variantConfirmed = true;
        closeSizes();

        if (stickyButton && !stickyButton.disabled) {
          setButtonLabel(stickyButtonText.getAttribute('data-default-text'));
        }
      });
    });

    productSection.addEventListener('variantChange', function (evt) {
      // Other options (e.g. color) may have just changed which sizes are
      // purchasable, so re-check availability every time, not just once.
      syncSizeAvailability();

      var variant = evt.detail && evt.detail.variant;
      if (!stickyButton || !stickyButtonText) {
        return;
      }

      if (variant && variant.available) {
        stickyButton.disabled = false;
        stickyButton.classList.remove('disabled');
        setButtonLabel(
          variantConfirmed
            ? stickyButtonText.getAttribute('data-default-text')
            : stickyButtonText.getAttribute('data-select-size-text')
        );
      } else if (variant) {
        stickyButton.disabled = true;
        stickyButton.classList.add('disabled');
        setButtonLabel((window.theme && theme.strings && theme.strings.soldOut) || stickyButtonText.textContent);
      } else {
        stickyButton.disabled = true;
        stickyButton.classList.add('disabled');
        setButtonLabel((window.theme && theme.strings && theme.strings.unavailable) || stickyButtonText.textContent);
      }
    });

    productSection.addEventListener('variantPriceChange', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant || !stickyPrice) {
        return;
      }

      stickyPrice.textContent = formatMoney(variant.price);

      if (stickyComparePrice) {
        if (variant.compare_at_price > variant.price) {
          stickyComparePrice.textContent = formatMoney(variant.compare_at_price);
          stickyComparePrice.hidden = false;
        } else {
          stickyComparePrice.hidden = true;
        }
      }
    });

    if (stickyButton) {
      stickyButton.addEventListener('click', function () {
        if (hasSizeOption && !variantConfirmed) {
          // Toggle the in-bar chip row open/closed instead of scrolling
          // the page — this is the core of the KHAITE-style pattern.
          if (sizesPanel && sizesPanel.classList.contains('sticky-atc__sizes--open')) {
            closeSizes();
          } else {
            openSizes();
          }
          return;
        }

        mainAddToCartBtn.click();
      });
    }

    // Once the item is actually in the cart, reset back to "Select a
    // Size" so the shopper can pick another size and add it too, instead
    // of the button silently re-adding the same size on a second tap.
    // This event bubbles from the real (hidden) product form, so it only
    // fires for *this* product's form — same scoping as variantChange.
    if (hasSizeOption) {
      productSection.addEventListener('ajaxProduct:added', function () {
        variantConfirmed = false;
        syncChipSelectedState(null);
        closeSizes();

        if (stickyButton && !stickyButton.disabled) {
          setButtonLabel(stickyButtonText.getAttribute('data-select-size-text'));
        }
      });
    }

    // Correct the chip row's sold-out state as soon as the page loads.
    // The liquid-rendered `disabled` attribute is only accurate for
    // single-option products, so for Color + Size (or any multi-option)
    // products this is what actually crosses out sizes that are sold out
    // for the color selected by default.
    syncSizeAvailability();

    // Belt-and-suspenders: watch the real picker's labels directly for
    // class changes, in case some other script updates availability
    // without going through the variantChange event (e.g. its own
    // DOMContentLoaded pass, which could run before or after this one).
    if (hasSizeOption && variantPicker && window.MutationObserver) {
      var availabilityObserver = new MutationObserver(syncSizeAvailability);
      availabilityObserver.observe(variantPicker, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
    }

    // Shopify's own theme-preview toolbar (shown on *.shopifypreview.com
    // links while reviewing a draft theme) docks to the bottom of the
    // screen too and would otherwise sit on top of this bar. Nudge the bar
    // up above it so it's visible while reviewing. This element doesn't
    // exist on the published, live site, so this is a no-op there.
    function avoidPreviewBar() {
      var previewBar = document.getElementById('PBarNextFrameWrapper');
      var height = previewBar ? previewBar.getBoundingClientRect().height : 0;
      bar.style.bottom = height ? height + 'px' : '';
    }

    avoidPreviewBar();
    window.addEventListener('resize', avoidPreviewBar);

    // Show the bar once the real Add to Cart button has fully scrolled
    // above the viewport, hide it again once that button is back in view.
    // Plain scroll-position math (rAF-throttled) rather than
    // IntersectionObserver, which doesn't fire reliably on some mobile
    // browsers for fixed-position siblings.
    var ticking = false;

    function updateVisibility() {
      var scrolledPast = mainAddToCartBtn.getBoundingClientRect().bottom < 0;
      bar.classList.toggle('sticky-atc--visible', scrolledPast);
      if (!scrolledPast) {
        closeSizes();
      }
      ticking = false;
    }

    function requestVisibilityUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateVisibility);
      }
    }

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
    window.addEventListener('resize', requestVisibilityUpdate);
    window.addEventListener('orientationchange', requestVisibilityUpdate);

    // Set the correct initial state (e.g. if the page loads mid-scroll
    // after a back/forward navigation).
    updateVisibility();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-sticky-atc]').forEach(initStickyAddToCart);
  });
})();
